
/*
  This is the main entry point of the Mimir UI application. As such, it may be
  useful to give an overview of the how the application initializes itself here:

  At the top-level the <App> component is simply a <router-view>, whose routes
  are by router.ts.

  The router has a beforeEach NavigationGuard, which will on the very first
  navigation attempt to fetch the service document and initialize global
  authentication state.

  The authentication initialization is implemented by PersistentUserSession
  using a region and userPoolId that together identifies a Cognito user pool,
  which provides a way for users to authenticate themselves and obtain refresh
  and JWT tokens.

  Secure Remote Password (SRP) is a protocol which allows the client UI to be
  responsible for getting the password from the user and using it to
  authenticate in a way that does not involve sending the password to the
  server.

  The authentication flow USER_SRP_AUTH is an AWS Cognito specific
  implementation of this protocol.

  The router beforeEach NavigationGuard then checks if the client has
  authentication credentials by calling initialize() on the PersistentUserSession
  instance.

  If there is no current user identity and refresh tokens stored in the browser
  local storage, the client will not be able to automatically acquire the JWT
  token needed to perform API calls, and as a result VueX store cannot be
  initialized.

  When this happens the <Layout> component will hide all the UI parts that
  require the store to be initialized, and will show the UI parts necessary to
  guide the user through the steps of logging in manually. This is done by
  having the Vue template directives react on the PersistentUserSession
  loginState property.

  The UI components such as Login.vue are shown and prompts the user for
  necessary information to complete the sign-in process. This information is
  passed as "account-input" events to App.vue, which handles these events partly
  by delegating to the PersistentUserSession instance.

  As soon as authentication has been established, initializeStore() is called
  prepare all the state needed for the rest of the UI to function.
*/

import { reactive } from 'vue';
import { getGlobalRouter } from '@mimir/web/router';
import { getReplacementLocationIfNeeded } from './replacementLocations';
import { initializeGlobalStore } from './store/initializeGlobalStore';
import { MimirClient } from '@mimir/common/client/mimirClient';
import noop from 'lodash/noop';
/*VUE3*/// import initializeVue from './initializeVue3';
/*VUE2*/ import initializeVue from './initializeVue2';
import type { ServiceCache } from '@mimir/common/client/serviceCache';
import { makeEmptyLoginState, PersistentUserSession, sessionAsAuthHeaderSource } from '@mimir/client-common/util/persistentUserSession';
import { installGlobalJwtTokenProvider } from '@mimir/client-common/aws/api/http';
import { mergeStateParamsIntoUrl } from '@mimir/client-common/util/urlParams';
import { makeAppRoutingState, type AppRoutingState } from './sharedState/appRoutingState';
import type { ApplicationContext } from '@mimir/common/context/applicationContext';
import type { BasicFetch } from '@mimir/common/types/basicFetch';
import { stayAuthorizedWhileSignedIn } from './ongoingAuthorization';
import App from './App.vue';
import { ConsoleLogger, NO_LOGGER } from '@mimir/common/util/logging';
import { validateComingFromStageQueryValue } from './util/globalLogin';
import type { BasicUploadManager } from '@mimir/common/upload/BasicUploadManager';
import { MainUploadManager } from './util/UploadManager';
import { uploadManagerOptions } from './store/uploadManagerOptions';
import { createQuickPreview } from './model/quickPreview';
import type { QuickPreview } from '@mimir/web/model/quickPreview';
import { createTimedMdf, type TimedMdfOps, type TimedMdfState } from './model/timedMdf';
import { createLogEventNotifier, type LogEventNotifier } from './model/logEventNotifier';
import { computeTimedMdfs } from './store/mdf-module';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type Timeline from '@mimir/client-common/timeline/Timeline';
import { getLogOutRedirectSession, setLogOutRedirectSession } from './util/sessionStorage';
import { getRememberMeGlobalLogin, setRememberMeGlobalLogin } from './util/localStorage';
import { createBufferCache } from '@mimir/common/util/bufferCache';
import type { SegmentProvider } from '@mimir/cutter/frameProvider/demuxAndDecode/segmentedPacketProvider';
import { createMimirSegmentProvider } from './model/cutter/mimirSegmentProvider';
import { demuxTsSegment } from '@mimir/common/mpegts/mpegTsDemuxer';
import { OperationApiContext } from '@mimir/common/util/operationApiContext';
import { ExponentialBackoff } from '@mimir/common/async/resilience';
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import { ApiActions } from '@mimir/client-common/util/apiActions';
import { makeBrowserFetch } from './browser/browserFetch';
import type { PlayerServices } from '@mimir/web/model/playerServices';
import { makeItemPollingService } from '@mimir/client-common/polling/makeItemPollingService';
import { decodeBase64Sync } from './browser/base64';
import { portableClock } from '@mimir/common/async/timing';
import type { ItemPollingOps } from '@mimir/client-common/polling/itemPollingOps';
import { ItemDetailsState } from './model/itemDetailsState';

type AppProps = {
  session: PersistentUserSession,
  serviceCache: ServiceCache,
  playerServices: PlayerServices,
  appRoutingState: AppRoutingState,
  applicationContext: ApplicationContext,
  uploadManager: BasicUploadManager,
  quickPreview: QuickPreview,
  itemPollingOps: ItemPollingOps,
  itemDetailsState: ItemDetailsState,
  timedMdfState: TimedMdfState,
  timedMdfOps: TimedMdfOps,
  logEventNotifier: LogEventNotifier,
};

const setupStorageFromUrlParams = (): Storage => {
  const urlParams = new URL(window.location.href).searchParams;
  const comingFromStage = urlParams.get('comingFromStage');
  const rememberMeParam = urlParams.get('rememberMe');

  if (comingFromStage && validateComingFromStageQueryValue(comingFromStage)) setLogOutRedirectSession(comingFromStage);

  if (rememberMeParam !== null) setRememberMeGlobalLogin(rememberMeParam === 'true');

  const hasLogOutRedirect = Boolean(getLogOutRedirectSession());
  const shouldRememberMe = getRememberMeGlobalLogin();

  // Decision logic:
  // - No logOutRedirect -> always localStorage
  // - Has logOutRedirect + rememberMe -> localStorage
  // - Has logOutRedirect + !rememberMe -> sessionStorage
  const useLocalStorage = !hasLogOutRedirect || shouldRememberMe;
  const storage = useLocalStorage ? window.localStorage : window.sessionStorage;

  console.log(`Using ${useLocalStorage ? 'localStorage' : 'sessionStorage'} for login session`);

  return storage;
};

/**
 * Passes any extra global login parameters from the URL query parameters
 * into the URL, so that they are available for subsequent requests.
 *
 * The extra parameter are found in the "state" URL query parameter as a JSON
 * object. This is used to pass parameters from an external identity provider
 * login back into Mimir.
 */
const passExtraGlobalLoginParamsToMimir = () => {
  const newUrl = mergeStateParamsIntoUrl(window.location.href);
  if (newUrl !== window.location.href) {
    window.history.replaceState(null, '', newUrl);
  }
};

export function initialize(serviceCache: ServiceCache, applicationContext: ApplicationContext): void {
  /*
  The login session object is created here at the top level entry point, since
  both the App component as well as the router beforeEach navigation guard need
  to consult the login session, and we don't want to resort to accessing global
  state.

  We make the login state of the session Vue observable, so that we can easily
  bind UI to that state.
*/

  const session = new PersistentUserSession(
    serviceCache,
    setupStorageFromUrlParams(),
    reactive(makeEmptyLoginState())
  );

  passExtraGlobalLoginParamsToMimir();

  /*
  Needed for modules such as clients/vuetify/src/util/api.ts and its
  underlying clients/mimir-common/src/aws/api/http.ts module which pokes at
  global state rather than taking in its dependencies.
*/
  installGlobalJwtTokenProvider({
    getJwtToken: () => session.getFreshJwt(),
    refresh: async () => { session.getFreshJwt(); }
  });

  const appRoutingState = reactive(makeAppRoutingState());

  const router = getGlobalRouter();

  const logger = new ConsoleLogger();
  const bufferCache = createBufferCache(NO_LOGGER);
  const apiContext = new OperationApiContext(globalBackendClient, new ExponentialBackoff());
  const apiActions = new ApiActions(apiContext);
  const fetch = makeBrowserFetch(window).fetch;

  const itemPollingOps = makeItemPollingService({
    logger,
    fetch,
    apiActions,
    clock: portableClock(),
    decodeBase64: decodeBase64Sync,
  });

  const playerServices: PlayerServices = {
    applicationContext,
    logger,
    bufferCache,
    createSegmentProviderForItem: (itemId: string): SegmentProvider => {
      return createMimirSegmentProvider({ apiActions, fetch, logger, itemPollingOps, clock: portableClock(), demuxTs: (bytes) => demuxTsSegment(bytes, false) }, itemId, false, false);
    },
    apiActions,
    fetch: makeBrowserFetch(window).fetch,
    itemPollingOps,
  };

  const store = initializeGlobalStore();

  const quickPreview = createQuickPreview();

  const itemDetailsState = new ItemDetailsState();

  const uploadManager = new MainUploadManager(applicationContext, uploadManagerOptions, quickPreview);

  /**
   * Global guard for each route. Public routes are not dealt here.
   */
  router.beforeEach(async (to, _from, next) => {
    const replacementLocation =
      await getReplacementLocationIfNeeded(
        applicationContext,
        serviceCache,
        session,
        quickPreview,
        itemDetailsState,
        store,
        appRoutingState,
        playerServices,
        to,
        window.location
      );
    next(replacementLocation);
  });


  const timedMdf = createTimedMdf({
    getTimedMdfs: () => computeTimedMdfs(store.state.mdf.mdfs),
    getOptionLists: () => store.getters.optionLists as OptionListDto[],
    getCurrentUserId: () => (store.getters.user as { id: string }).id,
    getCurrentItem: () => store.state.timeline.currentItem,
    getTimeline: () => store.getters.timeline as Timeline,
    clearTimelineState: () => store.commit('clearTimelineState'),
  });

  const props: AppProps = {
    session,
    serviceCache,
    playerServices,
    appRoutingState,
    applicationContext,
    uploadManager,
    quickPreview,
    itemPollingOps,
    itemDetailsState,
    timedMdfState: timedMdf.state,
    timedMdfOps: timedMdf.ops,
    logEventNotifier: createLogEventNotifier(),
  };

  initializeVue(App, props, store, router);

  window.LOG_LEVEL = 'VERBOSE';

  // Handles the S3 redirected URL to map to the actual route
  if (location.hash) {
    router.push(location.hash.substring(2)).catch(noop);
  }

  const mimirClient = new MimirClient({
    fetch: window.fetch.bind(window) as BasicFetch,
    service: {
      type: 'CachedServiceDocument',
      cache: serviceCache
    },
    credentials: {
      scheme: 'CredentialsSource',
      source: sessionAsAuthHeaderSource(session)
    },
    logger: new ConsoleLogger(),
  });

  stayAuthorizedWhileSignedIn(session.loginState, mimirClient);
}

